'use client'

/**
 * useDynamicActivities - Fetches active activities from Firestore
 *
 * Reads `activityPricing` collection where active == true.
 * Module-level cache prevents duplicate reads across components.
 *
 * Used to show dynamically-created activities (from admin) in:
 * - /guided-adventures/ page (merged with static adventures)
 * - Navbar dropdown (merged with static adventuresData)
 * - Mobile menu
 */

import { useEffect, useState } from 'react'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { pickEs } from '@/lib/utils/pickEs'

export interface DynamicActivity {
  id: string
  name: string
  slug: string
  description: string
  heroImage: string
  category: string
}

// Module-level cache — shared across all components on the same page
let cachedActivities: DynamicActivity[] | null = null
let fetchPromise: Promise<DynamicActivity[]> | null = null

async function fetchActivities(): Promise<DynamicActivity[]> {
  try {
    const q = query(
      collection(db, 'activityPricing'),
      where('active', '==', true)
    )
    const snapshot = await getDocs(q)
    return snapshot.docs
      .map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          name: pickEs(data.name) || doc.id,
          slug: data.slug || doc.id,
          description: pickEs(data.description) || pickEs(data.shortDescription) || '',
          heroImage: data.heroImage || '',
          category: (data.category || '').toString().toLowerCase(),
        }
      })
      // `activityPricing` is shared with services (category === 'transport',
      // e.g. ground-transportation). Adventures consumers — /guided-adventures
      // grid, navbar Adventures sidebar, mobile Adventures submenu — must not
      // list services. Belt-and-suspenders: drop 'service' too, in case the
      // category ever drifts during a migration.
      .filter(a => a.category !== 'transport' && a.category !== 'service')
  } catch (error) {
    console.error('[useDynamicActivities] Error fetching activities:', error)
    return []
  }
}

export function useDynamicActivities() {
  const [activities, setActivities] = useState<DynamicActivity[]>(cachedActivities || [])
  const [loaded, setLoaded] = useState(cachedActivities !== null)

  useEffect(() => {
    if (cachedActivities !== null) {
      setActivities(cachedActivities)
      setLoaded(true)
      return
    }

    if (!fetchPromise) {
      fetchPromise = fetchActivities()
    }

    let cancelled = false

    fetchPromise.then(data => {
      if (!cancelled) {
        cachedActivities = data
        setActivities(data)
        setLoaded(true)
      }
    })

    return () => {
      cancelled = true
    }
  }, [])

  return { activities, loaded }
}
